import React, { useState, useRef, useCallback } from 'react';
import './AudioRecorder.css';

// ─── Constants ────────────────────────────────────────────────
const TARGET_SR = 16000;  // Whisper expects 16 kHz
const VAD_FRAME_SAMPLES = 480;    // 30ms at 16 kHz
const SILENCE_FRAMES = 10;     // 10 × 30ms = 300ms silence → flush
const MIN_SPEECH_FRAMES = 8;      // 8 × 30ms = 240ms minimum — prevents flushing
// single-word clips that are too short for Whisper
// (0.6s clips caused hallucinated filler sentences)
const MAX_SPEECH_FRAMES = 100;    // ~3s — force flush runaway speech
const SPEECH_THRESHOLD = 0.015;  // RMS floor for voiced speech
const ARMING_MS = 350;           // grace period after mic starts — getUserMedia
// and AudioContext init produce silent/partial
// buffers for the first ~300ms; VAD frames during
// this window are discarded so early speech isn't lost

// ─── WAV encoder ──────────────────────────────────────────────
function encodeWAV(samples) {
  const len = samples.length;
  const buf = new ArrayBuffer(44 + len * 2);
  const view = new DataView(buf);
  const w4 = (o, s) => { for (let i = 0; i < 4; i++) view.setUint8(o + i, s.charCodeAt(i)); };

  w4(0, 'RIFF'); view.setUint32(4, 36 + len * 2, true);
  w4(8, 'WAVE'); w4(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, TARGET_SR, true);   // always 16 kHz
  view.setUint32(28, TARGET_SR * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  w4(36, 'data'); view.setUint32(40, len * 2, true);

  let o = 44;
  for (let i = 0; i < len; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    o += 2;
  }
  return new Blob([buf], { type: 'audio/wav' });
}

// ─── Downsample one callback chunk hwSR → 16 kHz (stateless) ─
function downsampleChunk(input, hwSR) {
  if (hwSR === TARGET_SR) return input;
  const ratio = hwSR / TARGET_SR;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const f = pos - idx;
    out[i] = (input[idx] ?? 0) * (1 - f) + (input[idx + 1] ?? 0) * f;
  }
  return out;
}

// ─── RMS of one VAD frame ─────────────────────────────────────
function frameRMS(buf, start) {
  let s = 0;
  const end = start + VAD_FRAME_SAMPLES;
  for (let i = start; i < end; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / VAD_FRAME_SAMPLES);
}

// ─── Overall RMS across all accumulated chunks ───────────────
// Rejects near-silence flushes that cause Whisper hallucination loops.
function bufferRMS(chunks) {
  let sum = 0, count = 0;
  for (const c of chunks) {
    for (let i = 0; i < c.length; i++) { sum += c[i] * c[i]; count++; }
  }
  return count > 0 ? Math.sqrt(sum / count) : 0;
}

// ─────────────────────────────────────────────────────────────
function AudioRecorder({ onAudioReady, isLoading, onListeningChange }) {
  const [isListening, setIsListening] = useState(false);
  const [vadState, setVadState] = useState('idle');     // idle | arming | speech | silence
  const [isProcessing, setIsProcessing] = useState(false);

  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const processorRef = useRef(null);
  const hwSRRef = useRef(44100);

  // carryRef holds leftover 16kHz samples (<480) that didn't fill a VAD frame.
  // ALWAYS at TARGET_SR — never mixed with hardware-rate data.
  const carryRef = useRef(new Float32Array(0));

  const speechFramesRef = useRef(0);
  const silenceFramesRef = useRef(0);
  const inSpeechRef = useRef(false);
  const dispatchingRef = useRef(false);

  // Utterance stored as array of small Float32Array chunks — concat only at flush
  const utteranceChunksRef = useRef([]);

  // True until ARMING_MS after mic start — VAD frames during this window
  // are captured into the buffer (in case speech starts immediately) but
  // don't count toward MIN_SPEECH_FRAMES, so silence-only arming periods
  // don't get treated as a discardable short utterance.
  const armingRef = useRef(false);
  const armingTimerRef = useRef(null);

  // ── Flush utterance to backend ───────────────────────────────
  const flushUtterance = useCallback(() => {
    if (dispatchingRef.current) return;

    const chunks = utteranceChunksRef.current;
    const nFrames = speechFramesRef.current;

    // Reset accumulator immediately so next utterance can start
    utteranceChunksRef.current = [];
    speechFramesRef.current = 0;
    silenceFramesRef.current = 0;
    inSpeechRef.current = false;
    setVadState('idle');

    if (nFrames < MIN_SPEECH_FRAMES) return; // too short — discard

    // Energy gate: reject near-silence flushes.
    // Whisper hallucinates repeated tokens when fed low-energy audio,
    // which causes the detection loop seen with SOCOM (75 corrections).
    if (bufferRMS(chunks) < 0.018) return;

    // Concatenate all chunks
    const totalLen = chunks.reduce((a, c) => a + c.length, 0);
    const merged = new Float32Array(totalLen);
    let pos = 0;
    for (const chunk of chunks) { merged.set(chunk, pos); pos += chunk.length; }

    // Tear down mic immediately when flushing to prevent recording extra silence/noise
    clearTimeout(armingTimerRef.current);
    armingRef.current = false;
    processorRef.current?.disconnect();
    streamRef.current?.getTracks().forEach(t => t.stop());
    ctxRef.current?.close();
    carryRef.current = new Float32Array(0);
    utteranceChunksRef.current = [];
    inSpeechRef.current = false;
    setIsListening(false);
    setVadState('idle');

    dispatchingRef.current = true;
    setIsProcessing(true);
    const wav = encodeWAV(merged);
    Promise.resolve(onAudioReady(wav)).finally(() => {
      dispatchingRef.current = false;
      setIsProcessing(false);
    });
  }, [onAudioReady]);

  // ── Audio processing callback (~46ms cadence at 44100 Hz) ───
  const onAudioProcess = useCallback((e) => {
    // Step 1: downsample THIS callback's chunk only — fully stateless
    const ds = downsampleChunk(e.inputBuffer.getChannelData(0), hwSRRef.current);

    // Step 2: prepend carry from previous callback
    const carry = carryRef.current;
    const combined = new Float32Array(carry.length + ds.length);
    combined.set(carry);
    combined.set(ds, carry.length);

    // Step 3: process complete 30ms VAD frames
    let offset = 0;
    while (offset + VAD_FRAME_SAMPLES <= combined.length) {
      const voiced = frameRMS(combined, offset) > SPEECH_THRESHOLD;

      if (voiced) {
        if (!inSpeechRef.current) {
          inSpeechRef.current = true;
          // Only show "speech" state once arming has finished — during
          // arming we still capture frames (so a fast talker isn't clipped)
          // but the UI keeps showing "get ready" instead of flickering.
          if (!armingRef.current) setVadState('speech');
        }
        silenceFramesRef.current = 0;
        speechFramesRef.current += 1;
        utteranceChunksRef.current.push(combined.slice(offset, offset + VAD_FRAME_SAMPLES));
        if (speechFramesRef.current >= MAX_SPEECH_FRAMES) flushUtterance();

      } else if (inSpeechRef.current) {
        // Post-speech silence — keep frames so word endings aren't clipped
        silenceFramesRef.current += 1;
        utteranceChunksRef.current.push(combined.slice(offset, offset + VAD_FRAME_SAMPLES));
        setVadState('silence');
        if (silenceFramesRef.current >= SILENCE_FRAMES) flushUtterance();
      }
      // Pre-speech silence: discard

      offset += VAD_FRAME_SAMPLES;
    }

    // Step 4: save sub-frame remainder for next callback (16kHz only)
    carryRef.current = combined.slice(offset);
  }, [flushUtterance]);

  // ── Start ────────────────────────────────────────────────────
  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      streamRef.current = stream;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      hwSRRef.current = ctx.sampleRate;

      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(2048, 1, 1);
      processorRef.current = processor;
      processor.onaudioprocess = onAudioProcess;
      source.connect(processor);
      processor.connect(ctx.destination);

      carryRef.current = new Float32Array(0);
      utteranceChunksRef.current = [];
      speechFramesRef.current = 0;
      silenceFramesRef.current = 0;
      inSpeechRef.current = false;
      dispatchingRef.current = false;

      // Arm for ARMING_MS — getUserMedia/AudioContext init produces
      // silent or partial buffers briefly, so we show "get ready" and
      // suppress the speech/silence UI states until the mic has settled.
      armingRef.current = true;
      setIsListening(true);
      setVadState('arming');
      clearTimeout(armingTimerRef.current);
      armingTimerRef.current = setTimeout(() => {
        armingRef.current = false;
        setVadState(prev => (prev === 'arming' ? 'idle' : prev));
      }, ARMING_MS);
    } catch (err) {
      alert('Microphone error: ' + err.message);
    }
  };

  // ── Stop ─────────────────────────────────────────────────────
  const stopListening = () => {
    if (inSpeechRef.current && utteranceChunksRef.current.length > 0) flushUtterance();
    clearTimeout(armingTimerRef.current);
    armingRef.current = false;
    processorRef.current?.disconnect();
    streamRef.current?.getTracks().forEach(t => t.stop());
    ctxRef.current?.close();
    carryRef.current = new Float32Array(0);
    utteranceChunksRef.current = [];
    inSpeechRef.current = false;
    setIsListening(false);
    setVadState('idle');
  };



  React.useEffect(() => { onListeningChange?.(isListening); }, [isListening, onListeningChange]);

  const btnClass = [
    'mic-button',
    isListening ? 'listening' : '',
    isListening && vadState === 'speech' ? 'speaking' : '',
    isProcessing ? 'processing' : '',
  ].filter(Boolean).join(' ');

  const statusText = isProcessing ? 'Processing your audio…'
    : !isListening ? 'Click the microphone to start listening'
      : vadState === 'arming' ? 'Get ready — listening starts now…'
        : vadState === 'speech' ? 'Hearing you...'
          : vadState === 'silence' ? 'Processing...'
            : 'Listening — waiting for speech';

  const isArming = isListening && vadState === 'arming';

  return (
    <div className="audio-recorder">
      <div className="mic-button-container">
        <button
          onClick={isListening ? stopListening : startListening}
          className={btnClass}
          disabled={isLoading || isProcessing}
          title={
            isProcessing ? 'Processing…'
              : isListening ? 'Stop Listening'
                : 'Start Listening'
          }
        >
          {isProcessing ? (
            <span className="mic-spinner" aria-label="Processing" />
          ) : isArming ? (
            <span className="mic-dots" aria-label="Getting ready">
              <span className="mic-dot" />
              <span className="mic-dot" />
              <span className="mic-dot" />
            </span>
          ) : (
            <img src="/mic.png" alt="Microphone" className="mic-icon" />
          )}
        </button>
      </div>
      <p className="mic-status">{statusText}</p>
    </div>
  );
}

export default AudioRecorder;